import { useToast } from 'components/Toast/ToastProvider';
import { useDirtyChecker } from 'hooks/useDirtyChecker';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { formatDateWithMonthYear } from 'utils';
import { contractsService } from '../../../api/services/contracts';
import {
  MILESTONE_FORM_DEFAULT_VALUES,
  SEVERITY,
  VALIDATION_TYPES,
} from '../constants';
import {
  calculateMilestoneDuration,
  calculateTotalAmount,
  checkChronologicalOrder,
  findDuplicateDates,
} from '../utils/helper';

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * useMilestones - Custom hook for managing contract milestones
 *
 * Features:
 * - CRUD operations for milestones
 * - Form validation and error handling
 * - Dirty state tracking for unsaved changes
 * - Sort order management with position insertion
 * - Real-time validation warnings
 * - Bulk save operations with API integration
 *
 * @param {Array} initialMilestones - Initial milestone data from API
 * @param {Object} contract - Contract object containing public_id and expected_duration_days
 * @param {number|string} totalProjectAmount - Total project budget for validation
 *
 * @returns {Object} Hook interface containing state, form methods, and actions
 */
const useMilestones = (
  initialMilestones = [],
  contract,
  totalProjectAmount
) => {
  // ==================== STATE MANAGEMENT ====================
  const [milestoneList, setMilestoneList] = useState(initialMilestones);
  const [isSaving, setIsSaving] = useState(false);
  const [deletedMilestones, setDeletedMilestones] = useState([]);
  const [updatedMilestones, setUpdatedMilestones] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState(null);

  const { showSuccess, showError } = useToast();

  // ==================== FORM MANAGEMENT ====================
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'all',
    defaultValues: MILESTONE_FORM_DEFAULT_VALUES,
  });

  const editingId = useWatch({ control, name: 'editingId' });

  // ==================== COMPUTED VALUES ====================
  const editingMilestone = useMemo(
    () => milestoneList.find((milestone) => milestone.id === editingId) || null,
    [milestoneList, editingId]
  );

  const {
    isDirty: hasMilestoneChanges,
    reset: resetDirtyChecker,
    updatedItems,
  } = useDirtyChecker(initialMilestones, milestoneList, {
    compareKeys: ['description', 'amount_usd', 'due_date'],
  });

  const validationWarnings = useMemo(() => {
    const warnings = [];

    // ==================== AMOUNT VALIDATION ====================
    if (totalProjectAmount) {
      const totalMilestoneAmount = calculateTotalAmount(milestoneList);
      const projectAmount = parseFloat(totalProjectAmount);

      // Only check for amount mismatch - WARNING
      if (totalMilestoneAmount !== projectAmount) {
        warnings.push({
          type: VALIDATION_TYPES.AMOUNT_MISMATCH,
          message: `Total milestone amount ($${totalMilestoneAmount.toLocaleString()}) does not match project amount ($${projectAmount.toLocaleString()})`,
          severity: SEVERITY.WARNING,
        });
      }
    }

    // ==================== DATE VALIDATION ====================

    // Check for duplicate dates - ERROR
    const duplicateDates = findDuplicateDates(milestoneList);
    duplicateDates.forEach((date) => {
      warnings.push({
        type: VALIDATION_TYPES.DUPLICATE_DATE,
        message: `Multiple milestones have the same due date: ${formatDateWithMonthYear(
          date
        )}`,
        severity: SEVERITY.ERROR,
      });
    });

    // Chronological order validation - ERROR
    warnings.push(
      ...checkChronologicalOrder(
        milestoneList,
        formatDateWithMonthYear,
        VALIDATION_TYPES,
        SEVERITY
      )
    );

    return warnings;
  }, [milestoneList, totalProjectAmount]);

  const hasChanges = useMemo(
    () =>
      hasMilestoneChanges &&
      !validationWarnings.some((w) => w.severity === SEVERITY.ERROR),
    [hasMilestoneChanges, validationWarnings]
  );

  // ==================== EFFECTS ====================
  useEffect(() => {
    setMilestoneList(initialMilestones);
    setDeletedMilestones([]);
  }, [initialMilestones]);

  useEffect(() => {
    if (!isModalOpen) {
      reset(MILESTONE_FORM_DEFAULT_VALUES);
    }
  }, [isModalOpen, reset]);

  // ==================== EVENT HANDLERS ====================
  const handleEditMilestone = useCallback(
    (milestone) => {
      setValue('editingId', milestone.id);
      setValue('title', milestone.title || '');
      setValue('description', milestone.description || '');
      setValue('amount_usd', milestone.amount_usd || '');
      setValue('due_date', milestone.due_date?.split('T')[0] || '');
      setIsModalOpen(true);
    },
    [setValue]
  );

  const handleAddMilestone = useCallback(() => {
    setValue('editingId', null);
    reset(MILESTONE_FORM_DEFAULT_VALUES);
    setIsModalOpen(true);
  }, [reset, setValue]);

  const handleSaveMilestone = useCallback(
    (data) => {
      const { editingId } = data;

      let updatedMilestones;

      if (editingId) {
        // Update existing milestone
        updatedMilestones = milestoneList.map((milestone) =>
          milestone.id === editingId ? { ...milestone, ...data } : milestone
        );
      } else if (insertPosition !== null) {
        // Insert milestone between existing ones and update sort_order
        const beforeMilestones = milestoneList.slice(0, insertPosition);
        const afterMilestones = milestoneList.slice(insertPosition);

        // Calculate the new sort_order by finding the next available position
        let newSortOrder;
        if (afterMilestones.length > 0) {
          // Insert before the next milestone, use its sort_order
          newSortOrder = afterMilestones[0].sort_order || 0;
        } else if (beforeMilestones.length > 0) {
          // Insert at the end, use last sort_order + 1
          const lastSortOrder =
            beforeMilestones[beforeMilestones.length - 1].sort_order || 0;
          newSortOrder = lastSortOrder + 1;
        } else {
          // Empty list, start with 1
          newSortOrder = 1;
        }

        // Create the new milestone
        const newMilestone = {
          ...data,
          id: Date.now().toString(),
          is_completed: false,
          sort_order: newSortOrder,
        };

        // Increment sort_order for all subsequent milestones to make space
        const updatedAfterMilestones = afterMilestones.map((milestone) => {
          const oldSortOrder = milestone.sort_order || 0;
          const newSortOrder = oldSortOrder + 1;

          const updatedMilestone = {
            ...milestone,
            sort_order: newSortOrder,
          };

          // Track this milestone as updated (avoid duplicates)
          if (oldSortOrder !== newSortOrder) {
            setUpdatedMilestones((prev) => {
              // Remove any existing entry for this milestone and add the updated one
              const filtered = prev.filter((m) => m.id !== milestone.id);
              return [...filtered, updatedMilestone];
            });
          }

          return updatedMilestone;
        });

        updatedMilestones = [
          ...beforeMilestones,
          newMilestone,
          ...updatedAfterMilestones,
        ];
      } else {
        // Add milestone at the end
        const lastSortOrder =
          milestoneList.length > 0
            ? Math.max(...milestoneList.map((m) => m.sort_order || 0))
            : 0;

        updatedMilestones = [
          ...milestoneList,
          {
            ...data,
            id: Date.now().toString(),
            is_completed: false,
            sort_order: lastSortOrder + 1,
          },
        ];
      }

      setMilestoneList(updatedMilestones);
      setIsModalOpen(false);
      setInsertPosition(null); // Reset insert position after saving
      reset(MILESTONE_FORM_DEFAULT_VALUES);
    },
    [milestoneList, insertPosition, reset]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    reset(MILESTONE_FORM_DEFAULT_VALUES);
  }, [reset]);

  const handleDeleteMilestone = useCallback(
    (milestone) => {
      const updatedMilestones = milestoneList.filter(
        (m) => m.id !== milestone.id
      );

      // Only add to deletedMilestones if it existed in initialMilestones
      const wasOriginalMilestone = initialMilestones.some(
        (originalMilestone) => originalMilestone.id === milestone.id
      );

      if (wasOriginalMilestone) {
        setDeletedMilestones((prev) => [...prev, milestone.id]);
      }

      setMilestoneList(updatedMilestones);
    },
    [milestoneList, initialMilestones]
  );

  const handleSaveContractMilestones = useCallback(async () => {
    if (!contract?.public_id) {
      showError('No contract to save');
      return { success: false, error: 'No contract to save' };
    }

    const updated = [...(updatedItems || []), ...updatedMilestones];

    // Remove duplicates by keeping only the last occurrence of each milestone
    const deduplicatedUpdated = updated.filter(
      (milestone, index, self) =>
        index === self.findIndex((m) => m.id === milestone.id)
    );

    const deleted = deletedMilestones;

    if (deduplicatedUpdated.length === 0 && deleted.length === 0) {
      showError('No changes to save');
      return { success: false, error: 'No changes to save' };
    }

    setIsSaving(true);

    try {
      const newTotalBudget = calculateTotalAmount(milestoneList);
      const currentBudget = parseFloat(totalProjectAmount || 0);
      const shouldUpdateBudget =
        deleted.length > 0 || newTotalBudget !== currentBudget;

      // Calculate total duration from milestone dates using helper function
      const durationInfo = calculateMilestoneDuration(milestoneList, contract);
      const {
        totalDays: totalMilestoneDays,
        milestoneCount,
        firstMilestone,
        lastMilestone,
      } = durationInfo;

      const contractExpectedDays = contract?.expected_duration_days || 0;

      const payload = { updated: deduplicatedUpdated, deleted };
      if (shouldUpdateBudget) {
        payload.updated_budget = newTotalBudget;
      }

      // Add duration validation if milestone days don't match contract expected days
      if (
        totalMilestoneDays !== contractExpectedDays &&
        totalMilestoneDays > 0
      ) {
        payload.updated_duration_days = totalMilestoneDays;
      }

      const milestoneResult = await contractsService.bulkUpdateMilestones(
        contract.public_id,
        payload
      );

      if (milestoneResult.success) {
        showSuccess('Milestones saved successfully!');
        // Clear the updated milestones tracking after successful save
        handleClearUpdatedMilestones();
        return { success: true, message: milestoneResult.message };
      }

      throw new Error(milestoneResult.error || 'Failed to update milestones');
    } catch (error) {
      const errorMessage = error.message || 'Failed to update milestones';
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [
    contract?.public_id,
    contract?.expected_duration_days,
    milestoneList,
    totalProjectAmount,
    updatedItems,
    deletedMilestones,
    showSuccess,
    showError,
  ]);

  const handleCancelChanges = useCallback(() => {
    setMilestoneList(initialMilestones);
    resetDirtyChecker(initialMilestones);
  }, [initialMilestones, resetDirtyChecker]);

  const handleRestoreDeletedMilestone = useCallback((milestone) => {
    setDeletedMilestones((prev) => prev.filter((m) => m.id !== milestone.id));
    setMilestoneList((prev) => [...prev, milestone]);
  }, []);

  const handleClearDeletedMilestones = useCallback(() => {
    setDeletedMilestones([]);
  }, []);

  const handleClearUpdatedMilestones = useCallback(() => {
    setUpdatedMilestones([]);
  }, []);

  // ==================== RETURN INTERFACE ====================
  return {
    // State
    milestoneList,
    isSaving,
    deletedMilestones,
    updatedMilestones, // Track milestones with changed sort_order
    isModalOpen,
    editingMilestone,
    hasChanges,
    validationWarnings,
    durationInfo: calculateMilestoneDuration(milestoneList, contract),

    // Form methods
    control,
    handleSubmit,
    errors,
    setValue,
    reset,

    // Actions
    handleAddMilestone,
    handleEditMilestone,
    handleSaveMilestone,
    handleCloseModal,
    handleDeleteMilestone,
    handleSaveContractMilestones,
    handleCancelChanges,
    handleRestoreDeletedMilestone,
    handleClearDeletedMilestones,
    handleClearUpdatedMilestones,
    setInsertPosition, // Allow setting insert position from outside
  };
};

export default useMilestones;
export { SEVERITY, VALIDATION_TYPES };
